import React from "react";
import { Formik } from "formik";
import {
  Backdrop,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { postNotification } from "../../../components/utilities/Notifications";
import { BCPXmark } from "../../../components/icons/bcp/BCPIcons";
import GenericPicker from "../../../components/utilities/GenericPicker";
import HelpButton from "../../../components/utilities/HelpButton";
import { postData } from "../../../components/utilities/UtilityFunctions";

export default function TopCutModal(props) {
  const {
    open,
    setOpen,
    event,
    setEvent,
    setLoading,
    setTriggerHandlePairingStatus,
    setRoundUpdating,
  } = props;

  return (
    <Modal
      disableAutoFocus
      open={Boolean(open)}
      onClose={() => {
        setOpen(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      {event ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                Top Cut Settings
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              scoreCut: false,
              scoreCutValue: 0,
              playerCut: false,
              playerCutValue: 0,
              pairingType: "Single Elimination",
              otherPod: false,
            }}
            enableReinitialize
            onSubmit={async (values) => {
              try {
                if (values.scoreCutValue <= 0 && values.playerCutValue <= 0) {
                  postNotification(
                    "error",
                    "Please select your Top Cut settings"
                  );
                  return;
                }

                if (values.pairingType !== "Swiss" && values.otherPod) {
                  postNotification(
                    "error",
                    "Other pod is only available for Swiss pairings"
                  );
                  return;
                }

                setLoading(true);

                var body = {};
                if (values.scoreCutValue > 0) {
                  body.scoreCut = {
                    metric: "numWins",
                    value: values.scoreCutValue,
                  };
                }
                if (values.playerCutValue > 0) {
                  body.playerCut = values.playerCutValue;
                }

                if (values.pairingType === "Swiss") {
                  body.bracketPairings = false;
                } else if (values.pairingType === "Double Elimination") {
                  body.doubleElimination = true;
                } else {
                  //
                }
                if (values.otherPod) {
                  body.otherPod = values.otherPod;
                }
                if (setRoundUpdating) setRoundUpdating(true);

                var updatedEvent = await postData(
                  `/events/${event.id}/topCut`,
                  {
                    body: body,
                  }
                );
                setEvent({ ...event, ...updatedEvent });
                setTriggerHandlePairingStatus(true);
                return;
              } catch (err) {
                setLoading(false);
                postNotification("error", err);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Typography className="MuiFormControlLabel-label">
                          Cut by Number of Wins
                        </Typography>
                      }
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.scoreCut}
                          onChange={() => {
                            if (values.scoreCut) {
                              setFieldValue("scoreCutValue", 0);
                            }
                            setFieldValue("scoreCut", !values.scoreCut);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>
                  {values.scoreCut && (
                    <Grid item xs={12}>
                      <TextField
                        name="scoreCutValue"
                        label="Number of Wins"
                        value={values.scoreCutValue}
                        variant="outlined"
                        fullWidth
                        required
                        margin="normal"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched.scoreCutValue && errors.scoreCutValue
                        )}
                        helperText={
                          touched.scoreCutValue && errors.scoreCutValue
                        }
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Typography className="MuiFormControlLabel-label">
                          Cut by {event?.teamEvent ? "Team" : "Player"} Count
                        </Typography>
                      }
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.playerCut}
                          onChange={(e) => {
                            if (values.playerCut) {
                              setFieldValue("playerCutValue", 0);
                            } else {
                              setFieldValue("playerCutValue", 8);
                            }
                            setFieldValue("playerCut", !values.playerCut);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>
                  {values.playerCut && (
                    <Grid item xs={12}>
                      <GenericPicker
                        name="playerCutValue"
                        label="Number of Players"
                        value={values.playerCutValue}
                        handleChange={handleChange}
                        list={[2, 4, 8, 16, 32, 64, 128]}
                        inclAny={false}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <GenericPicker
                      name="pairingType"
                      label="Pairing Type"
                      value={values.pairingType}
                      handleChange={(e) => {
                        if (e.target.value !== "Swiss") {
                          setFieldValue("otherPod", false);
                        }
                        handleChange(e);
                      }}
                      list={[
                        "Single Elimination",
                        "Double Elimination",
                        "Swiss",
                      ]}
                      inclAny={false}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Typography className="MuiFormControlLabel-label">
                          Swiss/Bottom Bracket Continues{" "}
                          <HelpButton title="This feature is only available for a Swiss pairing type selected above. If selected, the players not in the top cut will continue their event remaining in the swiss bracket." />
                        </Typography>
                      }
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.otherPod}
                          onChange={(e) => {
                            setFieldValue("otherPod", !values.otherPod);
                          }}
                          disabled={
                            values.pairingType === "Swiss" ? false : true
                          }
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setOpen(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                    >
                      Make Top Cut
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
